import { useMemo, useState } from 'react'
import { NetworkStatus, type DocumentNode, type OperationVariables, type TypedDocumentNode } from '@apollo/client'
import { useQuery, type SkipToken } from '@apollo/client/react'
import { skipTokenUnless, type OptionsOrBuild } from 'lib/helpers/skipTokenUnless/skipTokenUnless'

interface GatedQuerySettings {
  /** Keep the last result while parked instead of reading as never run. */
  keepWhileParked?: boolean
}

interface Park {
  isParked: boolean
  hiddenData?: unknown
}

/**
 * `useQuery` parked on `skipToken` until `condition` is truthy. While parked it reads as never run, where Apollo
 * keeps the last `data`, a mid-request `loading: true`, and later hands the parked data back as `previousData`.
 * `refetch` isn't gated: on a query parked since mount it fires without variables, so guard it with the same condition.
 */
export function useGatedQuery<
  TData,
  TVariables extends OperationVariables,
  TValue,
  TOptions extends useQuery.Base.Options<TData, NoInfer<TVariables>>,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  condition: TValue,
  optionsOrBuild: OptionsOrBuild<
    TValue,
    TOptions & useQuery.OptionsFor<TData, TVariables, TOptions> & { skip?: never }
  >,
  settings?: GatedQuerySettings,
): useQuery.ResultForOptions<TData, TVariables, TOptions | SkipToken>
export function useGatedQuery(
  query: DocumentNode,
  condition: unknown,
  optionsOrBuild: OptionsOrBuild<unknown, useQuery.Options>,
  { keepWhileParked = false }: GatedQuerySettings = {},
): useQuery.Result {
  const result = useQuery(query, skipTokenUnless(condition, optionsOrBuild))
  const isParked = !condition
  const [park, setPark] = useState<Park>({ isParked })

  if (park.isParked !== isParked) {
    setPark({
      isParked,
      hiddenData: isParked && !keepWhileParked ? result.data ?? result.previousData : park.hiddenData,
    })
  } else if (!isParked && park.hiddenData !== undefined && result.data === park.hiddenData) {
    setPark({ isParked })
  }

  const hidesPreviousData = park.hiddenData !== undefined && result.previousData === park.hiddenData

  return useMemo(() => {
    if (!isParked) {
      return hidesPreviousData ? { ...result, previousData: undefined } : result
    }

    const settled = { ...result, loading: false, networkStatus: NetworkStatus.ready }

    return keepWhileParked ? settled : { ...settled, data: undefined, dataState: 'empty', previousData: undefined }
  }, [result, isParked, keepWhileParked, hidesPreviousData])
}
